import React, { useState, useEffect } from 'react';

import IncompleteCompany from 'components/IncompleteCompany';
import useEmergencyMessage from 'hooks/useEmergencyMessage';
import useGlobalBottomSheet from 'hooks/useGlobalBottomSheet';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { getCompany } from 'services/companyService';
import { getAllPaymentCards } from 'services/paymentMethodService';

const useIncompleteCompanyProfileMessage = () => {
  const { t } = useStoreTranslation();
  const user = useUserAccount();
  const emergencyMessage = useEmergencyMessage();
  const bottomSheet = useGlobalBottomSheet();

  const [isLoadingCompany, setLoadingCompany] = useState(false);

  useEffect(() => {
    const finishCompanyProfileMessage = async () => {
      try {
        if (!user || user?.type !== 'admin') return;

        setLoadingCompany(true);
        const [company, cards] = await Promise.all([getCompany(user.companyId), getAllPaymentCards()]);
        setLoadingCompany(false);

        const hasLineOfCredit = !!company.lineOfCreditAvailable;

        const billingCompleted = !!company.billingInformation?.email && !!company.address?.address;
        const paymentCompleted = hasLineOfCredit || cards.length > 0 || company.creditCardByUser;
        const isProfileCompleted = billingCompleted && paymentCompleted;
        const isBookingAllowed = !!company.bookingAllowed;

        if (isBookingAllowed && isProfileCompleted) return;

        const onCloseBottomSheet = () => {
          bottomSheet.reset();
          emergencyMessage.show();
        };

        emergencyMessage.set({
          message: isProfileCompleted
            ? t('emergencyMessages.companyProfileInAnalysis')
            : t('emergencyMessages.completeCompanyProfile'),
          onClick: () => {
            bottomSheet.set({
              content: (
                <IncompleteCompany
                  hasLineOfCredit={hasLineOfCredit}
                  billingCompleted={billingCompleted}
                  paymentCompleted={paymentCompleted}
                  onClose={onCloseBottomSheet}
                />
              ),
              onClose: onCloseBottomSheet,
              options: {
                noHeader: true,
                modal: true,
              },
            });
            emergencyMessage.hide();
          },
        });
      } catch (e) {
        setLoadingCompany(false);
      }
    };

    finishCompanyProfileMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isLoadingCompany };
};

export default useIncompleteCompanyProfileMessage;
